import { useEffect, useRef, useState, type SVGProps, type ReactNode } from "react"

export type Iphone15ProProps = SVGProps<SVGSVGElement> & {
  width?: number
  height?: number
  children?: ReactNode
}

// Scales children uniformly based on the screen container width
function ScaledContent({ children }: { children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const baseWidth = 389.5 // design width of screen area in px

    const apply = () => {
      const w = el.clientWidth || baseWidth
      const s = w / baseWidth
      setScale(s)
    }

    apply()

    const ro = new ResizeObserver(() => apply())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          width: 389.5,
          height: 843.5,
          pointerEvents: "auto",
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default function Iphone15Pro({
  width = 433,
  height = 882,
  children,
  ...props
}: Iphone15ProProps) {
  const screenLeft = 21.25
  const screenTop = 19.25
  const screenWidth = 389.5
  const screenHeight = 843.5
  const cornerRadius = 55.75

  const leftPercent = (screenLeft / width) * 100
  const topPercent = (screenTop / height) * 100
  const heightPercent = (screenHeight / height) * 100
  const borderRadiusX = (cornerRadius / screenWidth) * 100
  const borderRadiusY = (cornerRadius / screenHeight) * 100

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        aspectRatio: `${width}/${height}`,
      }}
      {...(props as any)}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", width: "100%", height: "100%" }}
      >
        <path
          d="M2 73C2 32.6832 34.6832 0 75 0H357C397.317 0 430 32.6832 430 73V809C430 849.317 397.317 882 357 882H75C34.6832 882 2 849.317 2 809V73Z"
          className="fill-[#E5E5E5] dark:fill-[#404040]"
        />
        <path
          d="M0 171C0 170.448 0.447715 170 1 170H3V204H1C0.447715 204 0 203.552 0 203V171Z"
          className="fill-[#E5E5E5] dark:fill-[#404040]"
        />
        <path
          d="M1 234C1 233.448 1.44772 233 2 233H3.5V300H2C1.44772 300 1 299.552 1 299V234Z"
          className="fill-[#E5E5E5] dark:fill-[#404040]"
        />
        <path
          d="M1 319C1 318.448 1.44772 318 2 318H3.5V385H2C1.44772 385 1 384.552 1 384V319Z"
          className="fill-[#E5E5E5] dark:fill-[#404040]"
        />
        <path
          d="M430 279H432C432.552 279 433 279.448 433 280V384C433 384.552 432.552 385 432 385H430V279Z"
          className="fill-[#E5E5E5] dark:fill-[#404040]"
        />
        <path
          d="M6 74C6 35.3401 37.3401 4 76 4H356C394.66 4 426 35.3401 426 74V808C426 846.66 394.66 878 356 878H76C37.3401 878 6 846.66 6 808V74Z"
          className="fill-[#F5F5F5] dark:fill-[#262626]"
        />
        <path
          opacity="0.5"
          d="M174 5H258V5.5C258 6.60457 257.105 7.5 256 7.5H176C174.895 7.5 174 6.60457 174 5.5V5Z"
          className="fill-[#D4D4D4] dark:fill-[#404040]"
        />
        <path
          d="M21.25 75C21.25 44.2101 46.2101 19.25 77 19.25H355C385.79 19.25 410.75 44.2101 410.75 75V807C410.75 837.79 385.79 862.75 355 862.75H77C46.2101 862.75 21.25 837.79 21.25 807V75Z"
          className="fill-white dark:fill-black"
        />

        {!children && (
          <rect
            x="21.25"
            y="19.25"
            width="389.5"
            height="843.5"
            rx="55.75"
            ry="55.75"
            className="fill-white dark:fill-black"
          />
        )}
      </svg>

      {/* Screen content container */}
      <div
        style={{
          position: "absolute",
          left: `${leftPercent}%`,
          top: `${topPercent}%`,
          width: `${(screenWidth / width) * 100}%`,
          height: `${heightPercent}%`,
          overflow: "hidden",
          borderRadius: `${borderRadiusX}% / ${borderRadiusY}%`,
          contain: "paint",
          isolation: "isolate",
        }}
        className="bg-white dark:bg-black"
      >
        {children && <ScaledContent>{children}</ScaledContent>}
      </div>

      {/* Edge mask */}
      <div
        style={{
          position: "absolute",
          left: `${leftPercent}%`,
          top: `${topPercent}%`,
          width: `${(screenWidth / width) * 100}%`,
          height: `${heightPercent}%`,
          borderRadius: `${borderRadiusX}% / ${borderRadiusY}%`,
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
          pointerEvents: "none",
          zIndex: 10,
        }}
      />
    </div>
  )
}
